import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Clock,
  Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const caseStudies = [
  {
    id: 1,
    title: "E-Commerce Platform Transformation",
    client: "RetailCo International",
    industry: "Retail & E-Commerce",
    challenge:
      "Legacy system handling 10K daily users couldn't scale during seasonal spikes, resulting in 40% cart abandonment.",
    solution:
      "Built cloud-native microservices architecture with auto-scaling, implemented Redis caching, and optimized database queries.",
    results: [
      "99.9% uptime during Black Friday",
      "3x faster page load times",
      "60% reduction in cart abandonment",
      "Handled 150K concurrent users",
    ],
    technologies: ["React", "Node.js", "AWS", "Redis", "PostgreSQL"],
    duration: "6 months",
    team: "12 engineers",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
  },
  {
    id: 2,
    title: "Healthcare Management System",
    client: "MediCare Plus",
    industry: "Healthcare",
    challenge:
      "Paper-based patient records causing delays, errors, and HIPAA compliance risks across 50+ facilities.",
    solution:
      "Developed secure, HIPAA-compliant electronic health records system with real-time sync, role-based access, and mobile apps for doctors.",
    results: [
      "100% digital record conversion",
      "80% faster patient check-in",
      "Zero compliance violations",
      "Adopted by 50+ clinics in 6 months",
    ],
    technologies: ["React Native", "Python", "Django", "MongoDB", "Azure"],
    duration: "9 months",
    team: "15 engineers",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop",
  },
  {
    id: 3,
    title: "Fintech Mobile Banking App",
    client: "NeoBank Solutions",
    industry: "Financial Services",
    challenge:
      "Traditional banking app had poor UX, slow transactions, and limited features compared to modern fintech competitors.",
    solution:
      "Redesigned mobile app with biometric authentication, instant transfers, AI-powered financial insights, and seamless integration with 100+ banks.",
    results: [
      "2M downloads in first year",
      "4.8★ app store rating",
      "95% customer satisfaction",
      "Transaction time reduced to 2 seconds",
    ],
    technologies: ["Flutter", "Firebase", "Node.js", "TensorFlow", "GCP"],
    duration: "12 months",
    team: "18 engineers",
    image:
      "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=600&fit=crop",
  },
  {
    id: 4,
    title: "Supply Chain Optimization Platform",
    client: "LogiTech Global",
    industry: "Logistics",
    challenge:
      "Manual tracking across 500+ warehouses led to inventory discrepancies, delays, and $2M annual losses.",
    solution:
      "Built IoT-enabled tracking system with real-time analytics, predictive maintenance, and automated reordering using ML algorithms.",
    results: [
      "$2.5M annual cost savings",
      "Real-time visibility across entire chain",
      "40% reduction in delivery delays",
      "Automated 80% of reordering",
    ],
    technologies: ["Vue.js", "Python", "TensorFlow", "IoT", "AWS"],
    duration: "10 months",
    team: "20 engineers",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop",
  },
];

export default function CaseStudiesPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="case-studies-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px]"
          />
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, delay: 1 }}
            className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-[150px]"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 text-center"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            Case Studies
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Success <span className="glow-text">Stories</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed"
          >
            Explore how we've helped businesses transform their digital presence
            and achieve measurable results through innovative technology
            solutions.
          </motion.p>
        </motion.div>
      </section>

      {/* Case Studies Grid */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <CaseStudyCard key={study.id} study={study} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-[#0a0f1e]">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help transform your business with
              innovative technology solutions.
            </p>
            <Button
              onClick={() => navigate("/contact")}
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-lg group"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

function CaseStudyCard({ study, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="group"
    >
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
          isEven ? "" : "lg:grid-flow-dense"
        }`}
      >
        {/* Image */}
        <div className={`relative ${isEven ? "" : "lg:col-start-2"}`}>
          <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
            <img
              src={study.image}
              alt={study.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-colors duration-500" />
        </div>

        {/* Content */}
        <div className={isEven ? "" : "lg:col-start-1 lg:row-start-1"}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-4">
            {study.industry}
          </div>
          <h3 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
            {study.title}
          </h3>
          <p className="text-slate-500 text-sm mb-6">{study.client}</p>

          <div className="space-y-6 mb-8">
            <div>
              <h4 className="font-semibold text-white text-sm uppercase tracking-wide mb-2">
                Challenge
              </h4>
              <p className="text-slate-400 leading-relaxed">
                {study.challenge}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm uppercase tracking-wide mb-2">
                Solution
              </h4>
              <p className="text-slate-400 leading-relaxed">{study.solution}</p>
            </div>
          </div>

          {/* Results */}
          <div className="mb-8">
            <h4 className="font-semibold text-white text-sm uppercase tracking-wide mb-4">
              Results
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {study.results.map((result, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-slate-400 text-sm"
                >
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{result}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <Clock className="w-4 h-4" />
              <span>{study.duration}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <Users className="w-4 h-4" />
              <span>{study.team}</span>
            </div>
          </div>

          {/* Technologies */}
          <div className="flex flex-wrap gap-2 mt-6">
            {study.technologies.map((tech) => (
              <span
                key={tech}
                className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
